import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Search,
  Clock,
  TrendingUp,
  Package,
  Building2,
  Tag,
  X,
} from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import axios from "axios";
import { useNavigate } from "@remix-run/react";

interface SearchSuggestion {
  type: "recent" | "popular" | "category" | "supplier" | "product";
  name: string;
  id?: string;
  slug?: string;
  subtitle?: string;
  category?: string;
  supplier?: string;
  price?: number;
  store_name?: string;
}

interface IGlobalSearchProps {
  category?: string;
}

export default function GlobalSearch(props: IGlobalSearchProps) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Debounce function
  const debounce = <T extends unknown[]>(
    func: (...args: T) => void,
    delay: number
  ) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: T) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Fetch suggestions from API
  const fetchSuggestions = useCallback(
    debounce(async (searchTerm: string) => {
      try {
        const response = await axios.get(
          `/api/search?q=${encodeURIComponent(searchTerm)}`
        );
        const results = [...response.data.items, ...response.data.stores];
        setSuggestions(results || []);
      } catch (error) {
        console.error("Failed to fetch suggestions", error);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (query.trim() !== "") {
      fetchSuggestions(query);
    } else {
      setSuggestions([]);
    }
  }, [query, fetchSuggestions, props.category]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            Math.min(prev + 1, suggestions.length - 1)
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, -1));
          break;
        case "Escape":
          setIsOpen(false);
          inputRef.current?.blur();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectedIndex, suggestions, query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions.length > 0) {
        // If a suggestion is selected, use that
        handleSuggestionClick(suggestions[selectedIndex]);
      } else if (query.trim()) {
        // If no suggestion is selected but there's a query, search for it
        handleSearch({ type: "category", name: query });
      }
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.name);
    setIsOpen(false);
    handleSearch(suggestion);
  };

  const handleSearch = (searchQuery: SearchSuggestion) => {
    // Redirect or perform search logic here
    setIsOpen(false);
    if (searchQuery.type === "supplier") {
      // Navigate to store using ID since there's no slug
      if (searchQuery.slug) {
        navigate(`/store/${searchQuery.slug}`);
      } else {
        navigate(`/store/${searchQuery.id}`);
      }
    }

    if (searchQuery.type === "category") {
      clearSearch();
      navigate(`/search?q=${searchQuery.name.toLocaleLowerCase()}`);
    }

    if (searchQuery.type === "product") {
      clearSearch();
      navigate(`/search?q=${searchQuery.name.toLocaleLowerCase()}`);
    }
  };

  const clearSearch = () => {
    setQuery("");
    setSuggestions([]);
    setSelectedIndex(-1);
    setIsOpen(true); // Keep dropdown open when clearing
    inputRef.current?.focus();
  };

  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case "recent":
        return <Clock className="h-4 w-4 text-muted-foreground" />;
      case "popular":
        return <TrendingUp className="h-4 w-4 text-muted-foreground" />;
      case "category":
        return <Tag className="h-4 w-4 text-muted-foreground" />;
      case "supplier":
        return <Building2 className="h-4 w-4 text-muted-foreground" />;
      default:
        return <Package className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div
      className="relative w-full min-w-0 max-w-2xl overflow-x-clip px-0 sm:px-6 md:px-0"
      ref={searchRef}
    >
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search for products, brands or suppliers"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            // Ensure dropdown is open when user starts typing
            if (e.target.value.trim() !== "") {
              setIsOpen(true);
            }
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleInputKeyDown}
          className="h-12 pl-10 pr-10 text-sm sm:text-base"
        />
        {query && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearSearch}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="py-2">
            {suggestions.map((suggestion, index) => (
              <div
                key={`suggestion-${index}`}
                className={`px-4 py-3 hover:bg-muted cursor-pointer flex items-center gap-3 ${
                  selectedIndex === index ? "bg-muted" : ""
                }`}
                onClick={() => handleSuggestionClick(suggestion)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSuggestionClick(suggestion);
                  }
                }}
                tabIndex={0}
                role="button"
              >
                {getSuggestionIcon(suggestion.type)}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {suggestion.name}
                  </div>
                  {suggestion.type === "product" ? (
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                      <span className="font-medium text-green-600">
                        ${suggestion.price?.toFixed(2)}
                      </span>
                      <span>•</span>
                      <span>by {suggestion.store_name}</span>
                    </div>
                  ) : suggestion.category ? (
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs px-2 py-0">
                        {suggestion.category}
                      </Badge>
                      {suggestion.supplier && (
                        <span>by {suggestion.supplier}</span>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
